import { currencyExponent, money, type CurrencyCode, type Money } from './money';
import { CoreError, err, ok, type Result } from './result';

/**
 * An exchange rate as a positive decimal: how many units of the target currency one unit
 * of the source currency buys (`EUR→USD = "1.0823"`). Strings are preferred because they
 * are stored verbatim on expenses; numbers are accepted and read via their shortest
 * round-trip decimal representation (`String(n)`), so `1.0823` is exactly 1.0823.
 */
export type Rate = string | number;

/**
 * A table of rates relative to `base`, in the shape returned by frankfurter.app / ECB:
 * `{ base: 'EUR', date: '2026-10-09', rates: { USD: 1.0823, JPY: 162.5 } }`.
 * `rates[X]` is the price of one `base` unit in `X`. The base itself may be omitted.
 */
export interface RateTable {
  readonly base: CurrencyCode;
  /** ISO date (YYYY-MM-DD) the rates refer to. */
  readonly date: string;
  readonly rates: Readonly<Record<CurrencyCode, Rate>>;
}

/**
 * Rounding used when a converted amount falls between two minor units.
 * - `half-even` (banker's rounding, the default): ties go to the even neighbour, so
 *   systematic bias cancels out over many conversions. 0.125 → 0.12, 0.135 → 0.14.
 * - `half-up`: ties go away from zero. 0.125 → 0.13, -0.125 → -0.13.
 */
export type RoundingMode = 'half-even' | 'half-up';

export const DEFAULT_ROUNDING: RoundingMode = 'half-even';

export type FxError =
  | { readonly code: 'MISSING_RATE'; readonly from: CurrencyCode; readonly to: CurrencyCode }
  | { readonly code: 'INVALID_RATE'; readonly rate: Rate }
  /** The converted amount does not fit in a safe integer. */
  | { readonly code: 'OUT_OF_RANGE'; readonly from: CurrencyCode; readonly to: CurrencyCode };

/** Smallest and largest rate accepted by `parseRate` (manual rates, ECB tables). */
export const MIN_RATE = '0.000000001';
export const MAX_RATE = '1000000000';

/** Exact positive rational number. */
interface Rational {
  readonly num: bigint;
  readonly den: bigint;
}

const RATE_RE = /^(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

function pow10(n: number): bigint {
  return 10n ** BigInt(n);
}

function parseRational(rate: Rate): Rational | null {
  if (typeof rate === 'number' && (!Number.isFinite(rate) || rate <= 0)) return null;
  const s = typeof rate === 'number' ? String(rate) : rate.trim();
  const m = RATE_RE.exec(s);
  if (!m) return null;
  const intPart = m[1] ?? '0';
  const fracPart = m[2] ?? '';
  const e = Number(m[3] ?? '0') - fracPart.length;
  let num = BigInt(intPart + fracPart);
  let den = 1n;
  if (e >= 0) num *= pow10(e);
  else den = pow10(-e);
  if (num === 0n) return null;
  return { num, den };
}

const MIN_RATIONAL: Rational = { num: 1n, den: 10n ** 9n };
const MAX_RATIONAL: Rational = { num: 10n ** 9n, den: 1n };

/** a < b for positive rationals. */
function lessThan(a: Rational, b: Rational): boolean {
  return a.num * b.den < b.num * a.den;
}

/**
 * Validates a rate (positive finite decimal between `MIN_RATE` and `MAX_RATE`) and returns it
 * in canonical string form (at most 15 significant digits).
 */
export function parseRate(rate: Rate): Result<string, FxError> {
  const r = parseRational(rate);
  if (r === null || lessThan(r, MIN_RATIONAL) || lessThan(MAX_RATIONAL, r)) {
    return err({ code: 'INVALID_RATE', rate });
  }
  return ok(rationalToDecimal(r, 15));
}

/** Integer division of n/d rounded to the nearest integer using `mode`. `d` must be > 0. */
function divRound(n: bigint, d: bigint, mode: RoundingMode): bigint {
  const negative = n < 0n;
  const a = negative ? -n : n;
  let q = a / d;
  const r = a % d;
  const twice = r * 2n;
  if (twice > d || (twice === d && (mode === 'half-up' || q % 2n === 1n))) q += 1n;
  return negative ? -q : q;
}

/** Formats a positive rational with at most `sig` significant digits (half-even), no trailing zeros. */
function rationalToDecimal(r: Rational, sig: number): string {
  // Find k such that 10^k <= num/den < 10^(k+1).
  let k = r.num.toString().length - r.den.toString().length;
  if (r.num * (k >= 0 ? 1n : pow10(-k)) < r.den * (k >= 0 ? pow10(k) : 1n)) k -= 1;
  const scale = sig - 1 - k; // number of decimal places to keep
  const digits =
    scale >= 0
      ? divRound(r.num * pow10(scale), r.den, 'half-even')
      : divRound(r.num, r.den * pow10(-scale), 'half-even') * pow10(-scale);
  if (scale <= 0) return digits.toString();
  const padded = digits.toString().padStart(scale + 1, '0');
  const intPart = padded.slice(0, -scale);
  const frac = padded.slice(-scale).replace(/0+$/, '');
  return frac === '' ? intPart : `${intPart}.${frac}`;
}

/**
 * Converts `amount` into `to` using an explicit `rate` (units of `to` per unit of
 * `amount.currency`). The math is exact rational arithmetic on BigInt; only the final
 * result is rounded to `to`'s minor unit using `rounding` (default half-even).
 * This is what to use with the rate stored on an expense.
 *
 * Throws `CoreError('INVALID_RATE')` if the rate is not a positive decimal and
 * `CoreError('OUT_OF_RANGE')` if the result is not a safe integer.
 */
export function convertMoney(
  amount: Money,
  to: CurrencyCode,
  rate: Rate,
  rounding: RoundingMode = DEFAULT_ROUNDING,
): Money {
  const r = parseRational(rate);
  if (r === null) throw new CoreError('INVALID_RATE', `Invalid exchange rate ${String(rate)}`);
  const fromExp = currencyExponent(amount.currency);
  const toExp = currencyExponent(to);
  const n = BigInt(amount.amount) * r.num * pow10(toExp);
  const d = r.den * pow10(fromExp);
  const result = Number(divRound(n, d, rounding));
  if (!Number.isSafeInteger(result)) {
    throw new CoreError('OUT_OF_RANGE', 'Converted amount exceeds the safe integer range');
  }
  return money(result, to);
}

function tableRational(table: RateTable, currency: CurrencyCode): Rational | null {
  if (currency === table.base) return { num: 1n, den: 1n };
  const raw = table.rates[currency];
  return raw === undefined ? null : parseRational(raw);
}

/** Default precision (significant digits) for derived cross rates. */
export const CROSS_RATE_SIGNIFICANT_DIGITS = 10;

/**
 * Returns the rate to convert `from` → `to` with `table`, as a decimal string to be stored
 * on an expense. Direct rates from the base are returned exactly (canonical form of
 * `rates[to]`); cross rates (`rates[to] / rates[from]`) and inverse rates are rounded
 * half-even to `significantDigits` (default 10).
 */
export function getRate(
  table: RateTable,
  from: CurrencyCode,
  to: CurrencyCode,
  significantDigits: number = CROSS_RATE_SIGNIFICANT_DIGITS,
): Result<string, FxError> {
  if (from === to) return ok('1');
  const a = tableRational(table, from);
  const b = tableRational(table, to);
  if (a === null || b === null) return err({ code: 'MISSING_RATE', from, to });
  // b.den is a power of ten, so enough digits to cover b.num make the decimal exact.
  if (from === table.base) {
    return ok(rationalToDecimal(b, Math.max(significantDigits, b.num.toString().length)));
  }
  return ok(rationalToDecimal({ num: b.num * a.den, den: b.den * a.num }, significantDigits));
}

/**
 * Converts using a rate table: derives the rate with `getRate` (so the result is identical
 * to storing that rate and calling `convertMoney` later). Never throws: a result outside
 * the safe integer range is `OUT_OF_RANGE`.
 */
export function convertWithTable(
  amount: Money,
  to: CurrencyCode,
  table: RateTable,
  rounding: RoundingMode = DEFAULT_ROUNDING,
): Result<Money, FxError> {
  const rate = getRate(table, amount.currency, to);
  if (!rate.ok) return rate;
  try {
    return ok(convertMoney(amount, to, rate.value, rounding));
  } catch (e) {
    if (e instanceof CoreError && e.code === 'OUT_OF_RANGE') {
      return err({ code: 'OUT_OF_RANGE', from: amount.currency, to });
    }
    throw e;
  }
}
